import styled, { keyframes } from 'styled-components';

const float = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
`;

const blink = keyframes`
    0%, 80%, 100% { opacity: 0.2; }
    40% { opacity: 1; }
`;

const ring = keyframes`
    to { transform: rotate(360deg); }
`;

// Overlay pokazywany podczas generowania treści przez AI.
export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const SectionHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  padding: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f9fafb' : '#111827')};
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#4b5563')};
`;

export const SectionDescription = styled.p`
  margin: 0;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SectionCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px 18px;
  background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.card : '#ffffff')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
`;

export const PromptField = styled.textarea`
  width: 100%;
  resize: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 13.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }

  &:focus {
    outline: none;
  }
`;

export const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
`;

export const TagButton = styled.button`
  padding: 3px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f9fafb')};
  font-family: inherit;
  font-size: 12px;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#9ca3af')};
  }
`;

export const AdvancedPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

export const AdvancedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const SliderHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

export const FieldCaption = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

export const AdvancedToggle = styled.button<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    transition: transform 0.15s ease;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  }
`;

export const GenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 9px;
  min-height: 26px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  font-family: inherit;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  background: #1a4d4d;
  color: #ffffff;
  border: 1px solid #1a4d4d;

  &:hover:not(:disabled) {
    background: #0f3333;
    border-color: #0f3333;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ErrorBanner = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.4;
  color: #991b1b;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(239, 68, 68, 0.35)' : '#fecaca')};
`;

// Overlay pokazywany podczas generowania treści przez AI.
export const LoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    text-align: center;
    padding: 24px;
    border-radius: 4px;
    z-index: 20;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(26, 31, 38, 0.82)' : 'rgba(255, 255, 255, 0.82)')};
    backdrop-filter: blur(3px);
`;

export const LoadingAvatarWrap = styled.div`
    position: relative;
    width: 76px;
    height: 76px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${float} 1.6s ease-in-out infinite;
`;

export const LoadingAvatar = styled.img`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #047857;
`;

// Pierścień ładowania okalający avatar.
export const LoadingRing = styled.span`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 3px solid transparent;
    border-top-color: #047857;
    border-right-color: #4d9e9e;
    animation: ${ring} 0.9s linear infinite;
`;

export const LoadingText = styled.p`
    font-size: 14px;
    font-weight: 300;
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 2px;

    span {
        animation: ${blink} 1.4s infinite both;
    }
    span:nth-child(2) { animation-delay: 0.2s; }
    span:nth-child(3) { animation-delay: 0.4s; }
`;
